import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmDealOption, DsmUpdateDealAddendumPayload } from '../../../models/dsm-deal-options-view.models';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AbilityService,
  BreakpointService,
  CurrencyFormatPipe,
  DropdownData,
  GenericDropdownComponent
} from '@zayan-erp/shared';
import { DatePipe } from '@angular/common';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { MessageModule } from '@openng/optimus-ui/message';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BuilderReferenceService } from 'libs/home-building/src/lib/services/builder-reference.service';
import { map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AddendumType } from '@zayan-erp/shared';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DsmApsDepositsComponent } from '../../dsm-aps-deposits/dsm-aps-deposits.component';
import { DsmApsAddendumDepositFormComponent } from '../dsm-aps-addendum-deposit-form/dsm-aps-addendum-deposit-form.component';
import { DividerModule } from '@openng/optimus-ui/divider';
import { TagModule } from '@openng/optimus-ui/tag';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { DsmAddendumIncentiveFormComponent } from '../dsm-addendum-incentive-form/dsm-addendum-incentive-form.component';
import { DsmChangesSavedComponent } from '../dsm-changes-saved/dsm-changes-saved.component';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

@Component({
  standalone: true,
  selector: 'dsm-aps-addendum-details',
  host: { class: 'flex flex-col h-full' },
  imports: [
    TooltipModule,
    ReactiveFormsModule,
    FormsModule,
    GenericDropdownComponent,
    ToggleSwitchModule,
    TextareaModule,
    SkeletonModule,
    MessageModule,
    InputNumberModule,
    DialogModule,
    DatePipe,
    CurrencyFormatPipe,
    TooltipModule,
    ButtonModule,
    DsmApsAddendumDepositFormComponent,
    DsmApsDepositsComponent,
    DividerModule,
    TagModule,
    DsmAddendumIncentiveFormComponent,
    DsmChangesSavedComponent,
    FloatLabelModule
  ],
  providers: [],
  templateUrl: './dsm-aps-addendum-details.component.html'
})
export class DsmApsAddendumDetailsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dealOptionsService = inject(DsmDealOptionsService);
  private readonly dealContext = inject(DsmApsDealContextService);
  private readonly fb = inject(FormBuilder);
  private readonly builderReferenceService = inject(BuilderReferenceService);
  private readonly abilityService = inject(AbilityService);
  private readonly toaster = inject(ToastrService);
  private readonly breakpointService = inject(BreakpointService);
  readonly lotIncentiveService = inject(DsmLotIncentiveService);

  readonly isMobile = toSignal(this.breakpointService.isMobile$, { initialValue: false });

  readonly AddendumType = AddendumType;
  readonly dealAddendum = input.required<DsmDealOption>();
  readonly isUnderContract = input<boolean>(false);
  /** Set when the deal itself is cancelled: its addendums are read-only, exactly as a locked one is. */
  readonly dealCancelled = input<boolean>(false);

  // LOA addendums have no buyer, so buyer approval only applies to INI addendums.
  readonly hideBuyerApproved = computed(() => this.dealAddendum().addendumType.name !== AddendumType.INI);

  onChanges = output<void>();
  onClose = output<void>();

  // Local writable signals for toggle states - synced with input but can be reverted on API failure
  readonly buyerApproved = linkedSignal(() => this.dealAddendum().buyerApproved);
  readonly builderApproved = linkedSignal(() => this.dealAddendum().builderApproved);
  // Locked for everyone (used to disable the rest of the form, including for super admins).
  readonly isLocked = computed(
    () => this.dealAddendum().isLocked || this.dealAddendum().isCancelled || this.dealCancelled()
  );

  readonly isCashDisabled = computed(() => this.dealAddendum().isCancelled || this.dealCancelled());
  // Like Cash, Include in APS stays open on a locked addendum (a spec Schedule PE the deal took over).
  readonly isIncludeInApsDisabled = computed(() => this.dealAddendum().isCancelled || this.dealCancelled());

  readonly hasValidOptions = computed(() => this.dealAddendum().products.some(product => !product.isCancelled));
  // Whether the current user can approve (set buyer/builder to true) — SuperAdmin and Admin only.
  readonly canApproveAddendumViaRbac = computed(() => this.abilityService.ability.can('dsm-approve-addendum', 'aps'));
  // Whether the current user can edit toggles when the addendum is locked — SuperAdmin only.
  readonly canEditWhenLocked = computed(() => this.abilityService.ability.can('dsm-edit-locked-addendum', 'aps'));
  // Locked and the current user cannot override it.
  readonly isLockedAndCannotEdit = computed(() => this.dealAddendum().isLocked && !this.canEditWhenLocked());

  isLoading = signal<boolean>(true);
  readonly receivedAmount = computed(() => this.dealAddendum().paymentsReceived ?? 0);
  readonly depositsView = viewChild(DsmApsDepositsComponent);
  readonly depositsDealId = computed(() => this.dealContext.currentDeal()?.id ?? '');

  isDepositsModalVisible = signal<boolean>(false);
  isApplyIncentiveModalVisible = signal<boolean>(false);
  showAddForm = signal<boolean>(false);
  readonly incentiveRemaining = this.lotIncentiveService.incentiveRemaining;

  bulkDiscountForm = this.fb.group({
    percentage: new FormControl<number | null>(null, [Validators.min(0), Validators.max(100)])
  });

  readonly approvalsSaved = signal(0);
  readonly detailsSaved = signal(0);
  readonly incentivesSaved = signal(0);

  constructor() {
    toObservable(computed(() => this.dealAddendum().id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isLoading.set(true);
        setTimeout(() => {
          this.isLoading.set(false);
        }, 800);
      });
    this.addendumForm
      .get('builderReferenceId')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateAddendum();
      });
  }

  readonly builderReferences = toSignal(
    this.builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  addendumForm = this.fb.group({
    builderReferenceId: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    inContract: new FormControl<boolean>(false),
    cash: new FormControl<boolean>(false),
    includeInAps: new FormControl<boolean>(false),
    basePrice: new FormControl<number | null>(null)
  });

  onAddendumReceivedEffect = effect(() => {
    const addendum = this.dealAddendum();
    if (addendum) {
      this.addendumForm.patchValue(
        {
          builderReferenceId: addendum.builderReference.id,
          notes: addendum.notes,
          inContract: addendum.inContract,
          cash: addendum.cash,
          includeInAps: addendum.includeInAps,
          basePrice: (addendum as any).basePrice || this.dealContext.currentDeal()?.elevation?.basePrice || null
        },
        { emitEvent: false }
      );
      this.addendumForm.markAsPristine();

      if (this.isLocked()) {
        this.addendumForm.disable({ emitEvent: false });
        if (!this.isCashDisabled()) {
          this.addendumForm.controls.cash.enable({ emitEvent: false });
        }
        if (!this.isIncludeInApsDisabled()) {
          this.addendumForm.controls.includeInAps.enable({ emitEvent: false });
        }
      } else {
        this.addendumForm.enable({ emitEvent: false });
      }
    }
  });

  applyBulkDiscount(): void {
    const percentageControl = this.bulkDiscountForm.controls.percentage;
    const pct = percentageControl.value;
    if (percentageControl.pristine || this.bulkDiscountForm.invalid || pct === null) {
      return;
    }
    this.dealOptionsService.applyDiscountToAll(this.dealAddendum().id, pct, false).subscribe(() => {
      percentageControl.markAsPristine();
      this.detailsSaved.update(n => n + 1);
      this.onChanges.emit();
    });
  }

  clearAllDiscounts(): void {
    this.dealOptionsService.clearAllDiscounts(this.dealAddendum().id, false).subscribe(() => {
      this.bulkDiscountForm.controls.percentage.setValue(null, { emitEvent: false });
      this.detailsSaved.update(n => n + 1);
      this.onChanges.emit();
    });
  }

  openDepositsModal(): void {
    this.isDepositsModalVisible.set(true);
    this.showAddForm.set(false);
  }

  closeDepositsModal(): void {
    this.isDepositsModalVisible.set(false);
  }

  onAddClick(): void {
    this.showAddForm.set(true);
  }

  onCancelAdd(): void {
    this.showAddForm.set(false);
  }

  onDepositSaved(): void {
    this.showAddForm.set(false);
    this.onChanges.emit();
    this.depositsView()?.reload();
  }

  onDepositsChanged(): void {
    this.onChanges.emit();
  }

  updateAddendum(): void {
    if (this.isLocked()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      return;
    }
    if (this.addendumForm.pristine) {
      return;
    }

    if (this.addendumForm.invalid) {
      this.addendumForm.markAllAsTouched();
      return;
    }

    const payload: DsmUpdateDealAddendumPayload = this.addendumForm.getRawValue() as DsmUpdateDealAddendumPayload;
    this.dealOptionsService
      .updateAddendum(this.dealAddendum().id, payload, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addendumForm.markAsPristine();
          this.detailsSaved.update(n => n + 1);
          this.onChanges.emit();
        }
      });
  }

  // Sends only `cash`, so it works on a locked addendum without touching any other field or the approvals.
  updateCash(value: boolean): void {
    const cashControl = this.addendumForm.controls.cash;
    if (this.isCashDisabled()) {
      cashControl.setValue(!value, { emitEvent: false });
      return;
    }

    this.dealOptionsService
      .updateAddendum(this.dealAddendum().id, { cash: value }, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          cashControl.markAsPristine();
          this.detailsSaved.update(n => n + 1);
          this.onChanges.emit();
        },
        error: () => {
          // Revert to original state on API failure
          cashControl.setValue(!value, { emitEvent: false });
        }
      });
  }

  // Sends only `includeInAps`, so it works on a locked addendum; the server refuses it once the APS has gone for signing.
  updateIncludeInAps(value: boolean): void {
    const includeInApsControl = this.addendumForm.controls.includeInAps;
    if (this.isIncludeInApsDisabled()) {
      includeInApsControl.setValue(!value, { emitEvent: false });
      return;
    }

    this.dealOptionsService
      .updateAddendum(this.dealAddendum().id, { includeInAps: value }, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          includeInApsControl.markAsPristine();
          this.detailsSaved.update(n => n + 1);
          this.onChanges.emit();
        },
        error: () => {
          // Revert to original state on API failure
          includeInApsControl.setValue(!value, { emitEvent: false });
        }
      });
  }

  updateBuyerApproval(value: boolean): void {
    this.buyerApproved.set(value);

    if (this.isLockedAndCannotEdit()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      this.buyerApproved.set(!value);
      return;
    }

    if (value && !this.canApproveAddendumViaRbac()) {
      this.toaster.error('You do not have permission to approve addendums.', 'Permission Denied');
      this.buyerApproved.set(false);
      return;
    }

    if (value && !this.canApproveAddendum(value, 'buyer')) {
      this.buyerApproved.set(false);
      return;
    }

    // when locked (both approved), isLockedAndCannotEdit above already restricts reverts to SuperAdmin;
    // when not locked, anyone who can approve (Admin/SuperAdmin) can also revert the buyer approval
    const currentValue = this.dealAddendum().buyerApproved ?? false;
    const isReverting = currentValue && !value;
    if (isReverting && !this.canApproveAddendumViaRbac()) {
      this.toaster.error('You do not have permission to revert the buyer approval.', 'Permission Denied');

      setTimeout(() => {
        this.buyerApproved.set(!value);
      }, 500);
      return;
    }

    this.dealOptionsService.toggleApproval(this.dealAddendum().id, { toggleBuyerApproved: value }, false).subscribe({
      next: () => {
        this.approvalsSaved.update(n => n + 1);
        this.onChanges.emit();
      },
      error: () => {
        // Revert to original state on API failure
        this.buyerApproved.set(!value);
      }
    });
  }

  updateBuilderApproval(value: boolean): void {
    this.builderApproved.set(value);

    if (this.isLockedAndCannotEdit()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      this.builderApproved.set(!value);
      return;
    }

    if (value && !this.canApproveAddendumViaRbac()) {
      this.toaster.error('You do not have permission to approve addendums.', 'Permission Denied');
      this.builderApproved.set(false);
      return;
    }

    if (value && !this.canApproveAddendum(value, 'builder')) {
      this.builderApproved.set(false);
      return;
    }

    // when locked (both approved), isLockedAndCannotEdit above already restricts reverts to SuperAdmin;
    // when not locked, anyone who can approve (Admin/SuperAdmin) can also revert the builder approval
    const currentValue = this.dealAddendum().builderApproved ?? false;
    const isReverting = currentValue && !value;
    if (isReverting && !this.canApproveAddendumViaRbac()) {
      this.toaster.error('You do not have permission to revert the builder approval.', 'Permission Denied');
      setTimeout(() => {
        this.builderApproved.set(!value);
      }, 500);
      return;
    }

    this.dealOptionsService.toggleApproval(this.dealAddendum().id, { toggleBuilderApproved: value }, false).subscribe({
      next: () => {
        this.approvalsSaved.update(n => n + 1);
        this.onChanges.emit();
      },
      error: () => {
        // Revert to original state on API failure
        this.builderApproved.set(!value);
      }
    });
  }

  private canApproveAddendum(value: boolean, approvalType: 'buyer' | 'builder'): boolean {
    if (this.dealAddendum().products.length === 0) {
      this.toaster.error('Cannot approve an empty addendum.');
      return false;
    }

    // if (approvalType === 'builder' && value && !this.dealAddendum().builderApprovedDate) {
    //   this.toaster.error('Builder approval cannot occur without an approval date.');
    //   return false;
    // }

    if (this.dealAddendum().builderApproved && !this.dealAddendum().builderApprovedDate) {
      this.toaster.error('Builder approval cannot occur without an approval date.');
      return false;
    }

    const hasMissingAttributeValues = this.dealAddendum().products.some(product => {
      if (!product.attributes?.length) {
        return false;
      }

      return product.attributes.some(attribute => !attribute.subCategoryAttributeValues?.length);
    });

    if (hasMissingAttributeValues) {
      this.toaster.error('Buyer or builder approval cannot occur while any option is missing an attribute value.');
      return false;
    }

    return true;
  }

  openApplyIncentiveModal(): void {
    this.isApplyIncentiveModalVisible.set(true);
  }

  closeApplyIncentiveModal(): void {
    this.isApplyIncentiveModalVisible.set(false);
  }

  onIncentiveSaved(): void {
    this.isApplyIncentiveModalVisible.set(false);
    this.incentivesSaved.update(n => n + 1);
    this.onChanges.emit();
    this.lotIncentiveService.reloadLotIncentive();
  }
}
